/**
 * usePageCopilotContext — small hook every route calls in a `useEffect`
 * to publish its identity to the global CopilotProvider.
 *
 * The Copilot panel reads this context to:
 *   - Render the breadcrumb chip in its header ("@ Plant Overview")
 *   - Resolve "this dashboard / entity / batch" in canned intents
 *   - Surface page-appropriate suggested prompts
 *
 * Usage in a route:
 *
 *   usePageCopilotContext({
 *     route: "/app/dashboards",
 *     label: "Dashboards",
 *   });
 *
 *   // Inside a dashboard detail view, publish the dashboard id too:
 *   usePageCopilotContext({
 *     route: `/app/dashboards/${id}`,
 *     label: dashboard.name,
 *     currentDashboardId: id,
 *   });
 *
 * The hook is safe to call from any authenticated route — if the
 * CopilotProvider isn't mounted (e.g. on /signup) it's a no-op.
 */

import { useEffect, useRef } from "react";
import {
  useOptionalCopilot,
  type PageCopilotContext,
} from "@/lib/copilot/store";

export function usePageCopilotContext(ctx: PageCopilotContext): void {
  const copilot = useOptionalCopilot();
  // Keep a ref to the latest copilot so the effect can call setPageContext
  // without listing `copilot` itself as a dependency. The provider value
  // changes identity on every mutation (it bumps `version`), so including
  // `copilot` in the dep array re-fires the effect → re-bumps version →
  // infinite render loop.
  const copilotRef = useRef(copilot);
  copilotRef.current = copilot;

  // Stable JSON-shape dependency so the effect only re-fires when the
  // context content actually changes (callers often pass an inline
  // object literal that would otherwise re-fire every render).
  const stableKey = JSON.stringify(ctx);
  useEffect(() => {
    const c = copilotRef.current;
    if (!c) return;
    c.setPageContext(ctx);
    // Don't clear on unmount — the next route's effect will set its own
    // context. Clearing here would briefly show a stale "no page"
    // state during the transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey]);
}
